import { useState } from "react";
import { Heart, DollarSign, Bell, BellRing } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Follow + reaction bar for a trade: follow, heart (like), dollar (profited).
 * Props:
 *   trade: trade object
 *   onUpdated: (partial) => void
 *   size: "sm" | "md"
 *   stopPropagation: for use inside clickable rows
 */
export default function TradeReactions({ trade, onUpdated, size = "md", stopPropagation = false }) {
  const [pending, setPending] = useState(null); // "follow" | "like" | "profit" | null

  const likesCount = trade.likes_count ?? (trade.likes?.length ?? 0);
  const profitsCount = trade.profits_count ?? (trade.profits?.length ?? 0);
  const followersCount = trade.followers_count ?? (trade.followers?.length ?? 0);
  const liked = !!trade.liked_by_me;
  const profited = !!trade.profited_by_me;
  const followed = !!trade.followed_by_me;

  const dims = size === "sm"
    ? { pad: "px-2 py-1", icon: "w-3.5 h-3.5", text: "text-[11px]", gap: "gap-1", spacing: "gap-1" }
    : { pad: "px-3 py-1.5", icon: "w-4 h-4", text: "text-xs", gap: "gap-1.5", spacing: "gap-2" };

  const toggle = async (kind, e) => {
    if (stopPropagation) { e.stopPropagation(); e.preventDefault(); }
    if (pending) return;
    setPending(kind);
    try {
      const { data } = await api.post(`/trades/${trade.id}/${kind}`);
      onUpdated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to update ${kind}`);
    } finally {
      setPending(null);
    }
  };

  return (
    <div className={`inline-flex items-center ${dims.spacing}`} data-testid="trade-reactions">
      <button
        onClick={(e) => toggle("follow", e)}
        disabled={pending === "follow"}
        data-testid="reaction-follow-btn"
        title={followed ? "Unfollow this trade" : "Follow this trade for updates"}
        className={`inline-flex items-center ${dims.gap} ${dims.pad} rounded-sm font-mono ${dims.text} border transition-colors ${
          followed
            ? "border-blue-500/40 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
            : "border-zinc-800 text-zinc-400 hover:border-blue-500/40 hover:text-blue-400"
        } disabled:opacity-50`}
      >
        {followed ? <BellRing className={dims.icon} /> : <Bell className={dims.icon} />}
        <span data-testid="reaction-follow-count">{followersCount}</span>
      </button>

      <button
        onClick={(e) => toggle("like", e)}
        disabled={pending === "like"}
        data-testid="reaction-like-btn"
        title={liked ? "Remove like" : "Like this trade"}
        className={`inline-flex items-center ${dims.gap} ${dims.pad} rounded-sm font-mono ${dims.text} border transition-colors ${
          liked
            ? "border-rose-500/40 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20"
            : "border-zinc-800 text-zinc-400 hover:border-rose-500/40 hover:text-rose-400"
        } disabled:opacity-50`}
      >
        <Heart className={`${dims.icon} ${liked ? "fill-rose-400" : ""}`} />
        <span data-testid="reaction-like-count">{likesCount}</span>
      </button>

      <button
        onClick={(e) => toggle("profit", e)}
        disabled={pending === "profit"}
        data-testid="reaction-profit-btn"
        title={profited ? "Undo profit vote" : "I made profit from this trade"}
        className={`inline-flex items-center ${dims.gap} ${dims.pad} rounded-sm font-mono ${dims.text} border transition-colors ${
          profited
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
            : "border-zinc-800 text-zinc-400 hover:border-emerald-500/40 hover:text-emerald-400"
        } disabled:opacity-50`}
      >
        <DollarSign className={`${dims.icon} ${profited ? "" : ""}`} />
        <span data-testid="reaction-profit-count">{profitsCount}</span>
      </button>
    </div>
  );
}
